import { VIDEO_SERVICE_YOUKU, VIDEO_SERVICE_YOUTUBE, VIDEO_SERVICE_LOCAL } from './constants.mjs'
import PlayerYoutube from '@kissui/helpers/src/playerYoutube'
import PlayerYouku from '@kissui/helpers/src/playerYouku'
import './components/youtube-overlay'

import { readEvent } from '@kissui/helpers/src/assets/js/eventDispatch'
import {
    EVENT_VIDEO,
    EVENT_POPIN_CLOSED,
    EVENT_POPIN_OPENED,
    EVENT_VIDEO_TOGGLE,
    VIDEO_ON_HOVER,
    VIDEO_ON_OUT
} from '@kissui/components'
import viewportHelper from '@kissui/helpers/src/viewport.helpers'
import createProps from '@kissui/helpers/src/props.helpers'
import { setAttributes } from '@kissui/helpers/src/utils'

class Video extends HTMLElement {
    #percentThresholds = [25, 50, 75, 90]
    #lastTrackedThreshold = 0
    #progressInterval
    #eventCategory = 'User Engagement'

    constructor() {
        super()

        this.videoId = null
        this.videoMobile = null
        this.currentVideo = null
        this.videoService = ''
        this.containerId = ''
        this.ccPolicyLanguage = null
        this.ccPolicyCountry = null
        this.ccLoadPolicy = null
        this.displayControls = 0 // not display by default
        this.disabledKeyboard = 1 // disabled by default
        this.isBackground = false
        this.autoPlay = 0
        this.loop = 0
        this.mute = 'false'
        this.playOnHover = 'false'
        this.allowPopinAutoplay = false

        this.isPlaying = false

        this.play = null
        this.pause = null
        this.stop = null
        this.moveTo = null

        this.loadInterupted = null
        this.controlsReady = null
        this.overlayElement = null

        this._externalControl = this._externalControl.bind(this)
    }

    connectedCallback() {
        const { video_id, video_mobile, container_id, video_service, auto_play } = createProps(
            this.attributes
        )
        this.videoId = video_id || this.videoId
        this.videoMobile = video_mobile || this.videoMobile
        this.containerId = container_id || this.containerId
        this.videoService = video_service || this.videoService
        this.currentVideo =
            viewportHelper.is.mobile && this.videoMobile !== '' && this.videoMobile !== null
                ? this.videoMobile
                : this.videoId

        this.getAttributes(auto_play)
        this.init()
    }

    getAttributes(auto_play) {
        this.popinId = this.getAttribute('popin_id') || this.popinId
        this.ccPolicyLanguage = this.getAttribute('ccPolicy_language') || this.ccPolicyLanguage
        this.ccPolicyCountry = this.getAttribute('ccPolicy_country') || this.ccPolicyCountry
        this.ccLoadPolicy = this.getAttribute('cc_load_policy') || this.ccLoadPolicy
        this.displayControls =
            parseInt(this.getAttribute('display_controls')) || this.displayControls
        this.isBackground = this.getAttribute('is_background') === 'true' || this.isBackground
        this.autoPlay = this.getAttribute('auto_play') === 'true' || auto_play ? 1 : this.autoPlay
        this.loop = this.getAttribute('loop') === 'true' ? 1 : this.loop
        this.mute = this.getAttribute('mute') || this.mute
        this.playOnHover = this.getAttribute('play_on_hover') || this.playOnHover
        this.allowPopinAutoplay =
            this.getAttribute('allow_popin_autoplay') === 'true' || this.allowPopinAutoplay
    }

    init() {
        this.unbindEvent()

        if (this.popinId !== '') {
            this.setPopinEvents()
        }

        if (this.playOnHover === 'true') {
            this.setHoverEvents()
        }

        if (this.currentVideo) {
            this.render()

            if (this.videoService === VIDEO_SERVICE_LOCAL) {
                this.initLocal()
            } else {
                this.initServiceVideo()
            }

            this.boundExternalControl = this._externalControl.bind(this)
            window.addEventListener(EVENT_VIDEO + this.containerId, this.boundExternalControl)
        }
    }

    initServiceVideo() {
        if (!this.controlsReady) {
            if (this.videoService === VIDEO_SERVICE_YOUKU) {
                this.initYouku()
            } else {
                this.initYoutubeOverlay()
            }
        }
    }

    setPopinEvents() {
        this.boundOnPopinClosed = this.onPopinClosed.bind(this)
        window.addEventListener(EVENT_POPIN_CLOSED, this.boundOnPopinClosed)
        this.boundOnPopinOpened = this.onPopinOpened.bind(this)
        window.addEventListener(EVENT_POPIN_OPENED, this.boundOnPopinOpened)
        this.boundOnTogglePlayback = this.onTogglePlayback.bind(this)
        window.addEventListener(EVENT_VIDEO_TOGGLE, this.boundOnTogglePlayback)
    }

    setHoverEvents() {
        this.boundOnHover = this.onHover.bind(this)
        window.addEventListener(VIDEO_ON_HOVER, this.boundOnHover)
        this.boundOnOut = this.onOut.bind(this)
        window.addEventListener(VIDEO_ON_OUT, this.boundOnOut)
    }

    popinValid(id) {
        return this.popinId !== undefined && this.popinId !== '' && id === this.popinId
    }

    async onPopinOpened(e) {
        if (!this.allowPopinAutoplay || !this.popinValid(e.detail.id)) {
            return
        }

        // YouTube: hide lazy-overlay and init video on popin open
        // YouTube: prevent recreating player when popin closed & reopened
        if (this.videoService === VIDEO_SERVICE_YOUTUBE && !this.controlsReady) {
            this.initYoutube()
            return
        }

        await this.controlsReady
        this.play()
    }

    async onPopinClosed(e) {
        if (this.popinValid(e.detail.id)) {
            await this.controlsReady

            this.videoService === VIDEO_SERVICE_YOUTUBE ? this.stop() : this.pause()
        }
    }
    async onHover(e) {
        if (e.detail.id === this.containerId) {
            await this.controlsReady
            this.mute()
            this.play()
            this.loop = 1
        }
    }
    async onOut(e) {
        if (e.detail.id === this.containerId) {
            await this.controlsReady
            this.stop()
        }
    }

    async onTogglePlayback(e) {
        if (this.popinValid(e.detail.id)) {
            await this.controlsReady
            if (this.isPlaying) {
                this.pause()
            } else {
                this.play()
            }
        }
    }

    initYouku() {
        // Todo : Youku is not playing on mobile
        // Todo : to CSS display is not good
        const self = this
        const _YkPlayer = new PlayerYouku()

        this.controlsReady = new Promise((resolve, reject) => {
            this.loadInterupted = reject
            _YkPlayer
                .attach(this.containerId, this.currentVideo, this.isBackground, this.autoPlay)
                .then(player => {
                    self.play = () => {
                        player.playVideo()
                    }
                    self.pause = () => player.pauseVideo()
                    self.stop = () => {
                        player.pauseVideo()
                    }

                    window.addEventListener(
                        'message',
                        function (e) {
                            const { msg, stateParam } = e.data

                            if (msg == 'state') {
                                if (stateParam === 'play') {
                                    self.isPlaying = true
                                } else if (stateParam === 'pause' || stateParam === 'stop') {
                                    self.isPlaying = false
                                }
                            }
                        },
                        false
                    )
                })
                .finally(() => resolve())
        })
    }

    initYoutubeOverlay() {
        if (this.autoPlay) {
            this.initYoutube()
            return
        }

        this.overlayElement = document.createElement('nb-youtube-overlay')
        setAttributes(this.overlayElement, { video_id: this.currentVideo })
        this.appendChild(this.overlayElement)
        this.overlayElement.addEventListener('click', () => this.initYoutube())
    }

    initYoutube() {
        const _YtPlayer = new PlayerYoutube()

        const additionalParams = {
            color: '#8F7247',
            controls: this.displayControls,
            disablekb: this.disabledKeyboard,
            fs: 1
        }

        if (this.loop) {
            additionalParams.loop = this.loop
            additionalParams.controls = 0
            additionalParams.playlist = this.currentVideo
        }

        if (this.ccPolicyLanguage !== null) {
            Object.assign(additionalParams, {
                cc_load_policy: this.ccLoadPolicy || 1,
                cc_lang_pref: this.ccPolicyLanguage
            })
        }

        this.controlsReady = new Promise((resolve, reject) => {
            this.loadInterupted = reject
            _YtPlayer
                .attach(
                    this.containerId,
                    this.currentVideo,
                    this.isBackground,
                    this.autoPlay,
                    additionalParams
                )
                .then(player => {
                    this.play = () => player.playVideo()
                    this.pause = () => player.pauseVideo()
                    this.mute = () => player.mute()
                    this.unMute = () => player.unMute()
                    this.stop = () => player.stopVideo()
                    this.moveTo = (time = 0) => player.seekTo(time, true)

                    player.addEventListener('onStateChange', event =>
                        this.onYoutubePlayerStateChange(player, event)
                    )

                    this.play()

                    if (this.overlayElement) {
                        this.overlayElement.removeEventListener('click', () => this.initYoutube())
                        this.overlayElement.remove()
                    }
                })
                .finally(() => resolve())
        })
    }

    onYoutubePlayerStateChange(player, event) {
        if (!player || !event || !window.YT) {
            return
        }

        const { PlayerState } = window.YT
        const { videoTitle } = player
        // -1 – unstarted
        // 0 – ended
        // 1 – playing
        // 2 – paused
        // 3 – buffering
        // 5 – video cued
        const state = event.data
        window.gtmDataObject = window.gtmDataObject || []

        if (state === PlayerState.PLAYING) {
            this.isPlaying = true

            window.gtmDataObject.push({
                event: 'video_playing',
                eventCategory: this.#eventCategory,
                eventAction: 'YouTube Video - Play',
                eventLabel: videoTitle,
                videoTitle
            })

            // Track video progress every second
            this.#progressInterval = setInterval(() => {
                this.trackVideoProgress(player, event)
            }, 1000)
        } else if (state === PlayerState.PAUSED) {
            this.isPlaying = false

            window.gtmDataObject.push({
                event: 'video_paused',
                eventCategory: this.#eventCategory,
                eventAction: 'YouTube Video - Pause',
                eventLabel: videoTitle,
                videoTitle
            })
        } else if (state === PlayerState.ENDED) {
            window.gtmDataObject.push({
                event: 'video_ended',
                eventCategory: this.#eventCategory,
                eventAction: 'YouTube Video - Complete',
                eventLabel: `Video - Complete - ${videoTitle}`,
                videoTitle
            })
            clearInterval(this.#progressInterval)
        }
    }

    trackVideoProgress(player, event) {
        const { PlayerState } = window.YT
        const { videoTitle, playerInfo } = player
        const state = event.data

        if (playerInfo?.duration && playerInfo?.currentTime) {
            const percentWatched = (playerInfo.currentTime / playerInfo.duration) * 100

            for (let threshold of this.#percentThresholds) {
                if (
                    state === PlayerState.PLAYING &&
                    percentWatched >= threshold &&
                    this.#lastTrackedThreshold < threshold
                ) {
                    window.gtmDataObject.push({
                        event: 'video_progress',
                        eventCategory: this.#eventCategory,
                        eventAction: 'YouTube Video - Progress',
                        eventLabel: `Video - Watched ${threshold}% - ${videoTitle}`,
                        percentWatched: threshold,
                        videoTitle
                    })
                    this.#lastTrackedThreshold = threshold
                }
            }
        }
    }

    initLocal() {
        const video = document.getElementById(this.containerId)
        this.play = () => video.play()
        this.pause = () => video.pause()
    }

    renderVideo() {
        if (this.videoService === VIDEO_SERVICE_LOCAL) {
            const autoPlay = this.autoPlay === 1 ? 'true' : 'false'
            const loop = this.loop === 1 ? 'true' : 'false'

            return `
                <video autoplay="${autoPlay}" muted="${this.mute}" loop="${loop}" id="${
                this.containerId
            }" ${this.displayControls === 1 ? `controls="true"` : ''}>
                    <source src="${this.currentVideo}" type="video/mp4">
                </video>
            `
        }

        return `<div id="${this.containerId}"></div>`
    }

    render() {
        this.innerHTML = `${this.renderVideo()}`
    }

    _externalControl(e) {
        const event = readEvent(e)
        const { action, time = null } = event

        switch (action) {
            case 'play':
                this.play()
                break
            case 'pause':
                this.pause()
                break
            case 'mute':
                this.mute()
                break
            case 'unmute':
                this.unMute()
                break
            case 'stop':
                this.stop()
                break
            case 'moveTo':
                this.moveTo(time)
                break
            default:
        }
    }

    unbindEvent() {
        if (this.popinId !== '') {
            window.removeEventListener(EVENT_POPIN_CLOSED, this.boundOnPopinClosed)
            window.removeEventListener(EVENT_POPIN_OPENED, this.boundOnPopinOpened)
            window.removeEventListener(EVENT_VIDEO_TOGGLE, this.boundOnTogglePlayback)
        }

        if (this.playOnHover === 'true') {
            window.removeEventListener(VIDEO_ON_HOVER, this.boundOnHover)
            window.removeEventListener(VIDEO_ON_OUT, this.boundOnOut)
        }

        if (this.currentVideo !== '') {
            window.removeEventListener(EVENT_VIDEO + this.containerId, this.boundExternalControl)
        }

        if (this.loadInterupted) {
            this.loadInterupted()
            this.loadInterupted = null
        }

        if (this.overlayElement) {
            this.overlayElement.removeEventListener('click', () => this.initYoutube())
        }
    }

    disconnectedCallback() {
        this.unbindEvent()
    }
}

customElements.get('nb-video') || customElements.define('nb-video', Video)

export default Video
